import { SelectionModel } from '@angular/cdk/collections';
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { PermissionEnum } from '@app/core/services/administrator/models/enum/permission.enum';
import { OrderItem } from '@app/core/services/transaction/model/order-item.model';
import { Order } from '@app/core/services/transaction/model/order.model';
import { OrderRefundItem, RefundOrderRequest } from '@app/core/services/transaction/model/refund-order-request.model';
import { RefundRequest } from '@app/core/services/transaction/model/refund-request.model';
import { Transaction } from '@app/core/services/transaction/model/transaction.model';
import { TransactionService } from '@app/core/services/transaction/transaction.service';
import { NotificationService } from '@app/core/services/util/notification.service';
import { UtilService } from '@app/core/services/util/util.service';
import { TranslateService } from '@ngx-translate/core';
import { OrderItemDetailsDialogComponent } from '../order-item-details-dialog/order-item-details-dialog.component';

@Component({
    selector: 'ap-transaction-refund-content',
    templateUrl: './transaction-refund-content.component.html',
    styleUrls: ['./transaction-refund-content.component.css'],
    standalone: false
})
export class TransactionRefundContentComponent implements OnInit {
  @Input() transaction: Transaction;
  @Input() order: Order;
  @Input() userView = false;

  Permissions = PermissionEnum;
  numericPattern = /^(?:0*\.[0]*[1-9][0-9]*|0*[1-9][0-9]*(?:\.[0-9]+)?)$/;

  /* BLOCK STATE */
  isSelectBlockActive = true;

  /* SELECT REFUND */
  displayedColumns = ['SELECT', 'STATE', 'PRODUCT', 'SERVICE_PROVIDER', 'QUANTITY', 'UNITARY_AMOUNT', 'AMOUNT', 'ACTIONS'];
  displayedColumnsFooter = ['SELECT', 'UNITARY_AMOUNT', 'AMOUNT', 'ACTIONS'];
  selection = new SelectionModel<OrderItem>(true, []);
  selectRefundForm: UntypedFormGroup;
  get commentSelect() { return this.selectRefundForm.get('commentSelect'); }

  /* MANUAL REFUND */
  manualRefundForm: UntypedFormGroup;
  get amountToRefund() { return this.manualRefundForm.get('amountToRefund'); }
  get commentManual() { return this.manualRefundForm.get('commentManual'); }

  constructor(private fb: UntypedFormBuilder, private dialog: MatDialog, private router: Router, private transactionService: TransactionService, private notificationService: NotificationService, private translateService: TranslateService, private utilService: UtilService) {}

  ngOnInit(): void {
    this.isSelectBlockActive = this.order && this.order.orderItems && this.order.orderItems.length > 0;
    if (this.order?.orderItems && this.order.orderItems.length > 0) {
      this.order.orderItems = this.order.orderItems.sort((a,b) => this.utilService.compare(a.orderItemId, b.orderItemId, true));
    }
    this.initForm();
  }

  /* USER VIEW */

  backToTransactions() {
    if (this.userView) {
      const url = sessionStorage.getItem('from_user_tabs');
      const filterState = sessionStorage.getItem('filter_on');
      filterState == 'true' ? this.router.navigate([url], { queryParams: { filter: 'on' } }) : this.router.navigate([url]);
    }
  }

  /* SELECT */

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.order.orderItems.filter(orderItem => orderItem.quantityRefunded == null || orderItem.quantityRefunded === 0).length;
    return numSelected === numRows;
  }

  masterToggle() {
    if (this.order?.orderItems) {
      this.isAllSelected() ? this.selection.clear() : this.order.orderItems.filter(orderItem => orderItem.quantityRefunded == null || orderItem.quantityRefunded === 0).forEach(row => this.selection.select(row));
    }
  }

  showOrderItemDetails(orderItem: OrderItem) {
    this.dialog.open(OrderItemDetailsDialogComponent, {
      panelClass: 'modal',
      width: '60%',
      data: {
        orderItem: orderItem
      }
    });
  }

  submitSelectedRefund() {
    if (this.selection.hasValue()) {

      const refundOrderRequest: RefundOrderRequest = {
        comment: this.commentSelect.value,
        items: []
      };

      this.selection.selected.forEach(orderItem => {
        if (!orderItem.quantityRefunded) {
          orderItem.quantityRefunded = 0;
        }

        const orderRefundItem: OrderRefundItem = {
          orderItemId: orderItem.orderItemId,
          quantity: orderItem.quantity - orderItem.quantityRefunded
        }

        refundOrderRequest.items.push(orderRefundItem);
      })

      this.transactionService.createRefundWithOrder(this.transaction.paymentId, refundOrderRequest).subscribe({
        next: (_refundedTransaction: Transaction) => {
          this.notificationService.notify('success', this.translateService.instant('TRANSACTIONS.REFUND.TOAST.SUCCESS'));
          this.navigateToTransactionDetails(this.transaction.paymentId);
        },
        error: _err => {
          if (_err.status === 403) {
            if (_err.error?.message?.includes('Unable to refund the payment with this amount')) {
              this.notificationService.notify('error', this.translateService.instant('TRANSACTIONS.REFUND.TOAST.ERROR_AMOUNT'));
              return;
            }
            if (_err.error?.message?.includes('has already quantity to refund')) {
              this.notificationService.notify('error', this.translateService.instant('TRANSACTIONS.REFUND.TOAST.ERROR_ORDER_ALREADY_REFUND'));
              return;
            }
          }
          this.notificationService.notify('error', this.translateService.instant('TRANSACTIONS.REFUND.TOAST.ERROR'));
        }
      });
    }
  }

  /* MANUAL REFUND */

  onSubmitManualRefundForm() {
    if (this.amountToRefund.value && !isNaN(this.amountToRefund.value) && this.amountToRefund.value > 0) {
      if (!this.transaction.amountRefunded || isNaN(this.transaction.amountRefunded)) {
        this.transaction.amountRefunded = 0;
      }

      if (this.transaction.amount - this.transaction.amountRefunded >= this.amountToRefund.value) {
        const refundBody: RefundRequest = {
          amount: this.amountToRefund.value,
          comment: this.commentManual.value
        }

        this.transactionService.createRefund(this.transaction.paymentId, refundBody).subscribe({
          next: (_refundedTransaction: Transaction) => {
            this.notificationService.notify('success', this.translateService.instant('TRANSACTIONS.REFUND.TOAST.SUCCESS'));
            this.navigateToTransactionDetails(this.transaction.paymentId);
          },
          error: _err => {
            if (_err.status === 403) {
              if (_err.error?.message?.includes('Unable to refund the payment with this amount')) {
                this.notificationService.notify('error', this.translateService.instant('TRANSACTIONS.REFUND.TOAST.ERROR_AMOUNT'))
                return;
              }
            }
            if (_err.status === 409) {
              if (_err.error?.message?.includes('Pending refund already exists')) {
                this.notificationService.notify('error', this.translateService.instant('TRANSACTIONS.REFUND.TOAST.ERROR_ALREADY_REFUND'))
                return;
              }
            }
            this.notificationService.notify('error', this.translateService.instant('TRANSACTIONS.REFUND.TOAST.ERROR'))
          }
        });

      }
    }
  }

  /* BLOCK STATE */

  onFocusInTable() {
    this.isSelectBlockActive = true;
  }

  onFocusInInput() {
    this.isSelectBlockActive = false;
  }

  /* OTHERS */

  navigateToTransactionDetails(id: number) {
    if (this.userView) {
      this.router.navigate(['/users', this.transaction.mobilityAccountId, 'all', 'transactions', id]);
    } else {
      this.router.navigate(['/transactions', id]);
    }
  }

  /* PRIVATE */

  private initForm(): void {
    this.manualRefundForm = this.fb.group({
      amountToRefund: this.fb.control('', [Validators.required, Validators.pattern(this.numericPattern), this.positiveAmount(this.transaction.amount, this.transaction.amountRefunded)]),
      commentManual: this.fb.control('', [Validators.required])
    });

    this.selectRefundForm = this.fb.group({
      commentSelect: this.fb.control('', [Validators.required])
    });
  }

  private positiveAmount(initialAmount: number, amountRefunded: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control || !control.value) {
        return null;
      }

      if (!amountRefunded || isNaN(amountRefunded)) {
        amountRefunded = 0;
      }

      if (initialAmount - amountRefunded < control.value) {
        return { positiveAmount: true }
      }
      return null;
    };
  }


}
